// The landing page's motion (pages/home): the hero console, the raw-material
// decoder, the highlighter marks, the team tabs and the feature carousels.
// The server renders every piece complete; this module only replays it.
// [data-play] pieces not yet scrolled past are "armed" (reset to their start
// state) at load and played once their [data-play-trigger] (or they
// themselves) is half in view, so a piece already on screen on a tall window
// still plays. The team tabs and carousels follow the page scroll until
// someone uses them (driveByScroll). Under prefers-reduced-motion the tabs
// and carousels still switch, instantly, and nothing else moves.

const DWELL_MS = 7500 // per console answer, typing included

// Scroll-driven selection. Until someone uses a component, its selection
// follows the page scroll: the first item while the whole component has just
// come into view at the bottom of the window, the last once its top reaches
// the top, equal bands of scroll in between. Only on wide screens and only
// while the component fits the window (taller than the window, the two ends
// arrive in the wrong order). The returned function hands the component to
// the user for good.
const SCROLL_DRIVE = window.matchMedia("(min-width: 1024px)")
const drivers = new Set()
let driveQueued = false

function driveByScroll(el, count, apply) {
  const driver = { el, count, apply, last: 0 }
  if (!drivers.size) {
    window.addEventListener("scroll", queueDrive, { passive: true })
    window.addEventListener("resize", queueDrive)
  }
  drivers.add(driver)
  runDriver(driver, true)
  return () => drivers.delete(driver)
}

function queueDrive() {
  if (driveQueued) return
  driveQueued = true
  requestAnimationFrame(() => {
    driveQueued = false
    drivers.forEach((driver) => runDriver(driver, false))
  })
}

function runDriver(driver, instant) {
  if (!SCROLL_DRIVE.matches) return
  const range = window.innerHeight - driver.el.offsetHeight
  if (range <= 0) return
  const top = driver.el.getBoundingClientRect().top
  const progress = Math.min(1, Math.max(0, (range - top) / range))
  const i = Math.min(driver.count - 1, Math.floor(progress * driver.count))
  if (i === driver.last) return
  driver.last = i
  driver.apply(i, instant)
}

export function initLanding() {
  const motion = !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  if (motion) initPlays()
  document.querySelectorAll("[data-console]").forEach((el) => initConsole(el, motion))
  document.querySelectorAll("[data-tabs]").forEach((el) => initTabs(el, motion))
  document.querySelectorAll("[data-carousel]").forEach((el) => initCarousel(el, motion))
}

function initPlays() {
  const targets = [...document.querySelectorAll("[data-play]")]
  if (!targets.length || !("IntersectionObserver" in window)) return
  const owners = new Map() // trigger -> the [data-play] element it starts
  const io = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue
      const el = owners.get(entry.target)
      io.unobserve(entry.target)
      el.removeAttribute("data-armed")
      el.setAttribute("data-playing", "")
      if (el.classList.contains("decode")) decodeFields(el)
    }
  }, { rootMargin: "0px 0px -15% 0px", threshold: 0.5 })
  for (const el of targets) {
    // Already scrolled past (a reload mid-page): leave it resolved.
    if (el.getBoundingClientRect().bottom < 0) continue
    el.setAttribute("data-armed", "")
    const trigger = el.querySelector("[data-play-trigger]") || el
    owners.set(trigger, el)
    io.observe(trigger)
  }
}

// The decoder's resolved fields churn as noise, then settle left to right at
// the moment their token lights up (the row's --d, shared with the CSS).
// Monospace and whitespace kept in place, so the noise wraps like the text.
const GLYPHS = "ABCDEFGHJKLMNPQRSTUVWXYZ0123456789#/*"
const SETTLE_MS = 550

function decodeFields(root) {
  root.querySelectorAll(".decode-field").forEach((row) => {
    const cell = row.querySelector("td")
    const full = [...cell.textContent]
    const at = (parseFloat(getComputedStyle(row).getPropertyValue("--d")) || 0) * 1000
    const noise = (ch) => (/\s/.test(ch) ? ch : GLYPHS[Math.floor(Math.random() * GLYPHS.length)])
    const start = performance.now()
    let drawn = -Infinity
    const frame = (now) => {
      const t = now - start
      if (t >= at + SETTLE_MS) { cell.textContent = full.join(""); return }
      if (now - drawn >= 50) {
        drawn = now
        const fixed = t < at ? 0 : Math.floor((full.length * (t - at)) / SETTLE_MS)
        cell.textContent = full.slice(0, fixed).join("") + full.slice(fixed).map(noise).join("")
      }
      requestAnimationFrame(frame)
    }
    requestAnimationFrame(frame)
  })
}

// Arrow-key roving focus shared by both tab sets.
function keyTarget(key, count, current, vertical) {
  if (key === "ArrowRight" || (vertical && key === "ArrowDown")) return (current + 1) % count
  if (key === "ArrowLeft" || (vertical && key === "ArrowUp")) return (current - 1 + count) % count
  if (key === "Home") return 0
  if (key === "End") return count - 1
  return null
}

// Keeps the chosen tab visible when its list scrolls sideways (phones),
// without ever scrolling the page itself.
function revealTab(list, tab) {
  if (list.scrollWidth <= list.clientWidth) return
  list.scrollTo({ left: tab.offsetLeft - 16, behavior: "smooth" })
}

// Numbers count up to the figure the server rendered, keeping its prefix,
// suffix and decimals ("$18.1M", "86.7%", "212").
function countUp(scope) {
  scope.querySelectorAll("[data-count]").forEach((el) => {
    const full = (el.dataset.full ??= el.textContent)
    const match = full.match(/^(\D*)([\d,]*\.?\d+)(.*)$/)
    if (!match) return
    const [, prefix, digits, suffix] = match
    const target = parseFloat(digits.replace(/,/g, ""))
    const decimals = (digits.split(".")[1] || "").length
    const start = performance.now()
    const frame = (now) => {
      const k = Math.min(1, (now - start) / 1000)
      if (k === 1) { el.textContent = full; return }
      const value = target * (1 - Math.pow(1 - k, 3))
      el.textContent = prefix + value.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix
      requestAnimationFrame(frame)
    }
    requestAnimationFrame(frame)
  })
}

// The hero console. The dwell timer IS the active tab's progress-bar
// animation: its animationend advances to the next answer, so pausing it in
// CSS (hover over the answer, keyboard focus, off-screen, the pause button)
// pauses the rotation with no timer bookkeeping.
function initConsole(root, motion) {
  const list = root.querySelector('[role="tablist"]')
  const tabs = [...list.querySelectorAll('[role="tab"]')]
  const panels = tabs.map((tab) => document.getElementById(tab.getAttribute("aria-controls")))
  const toggle = root.querySelector("[data-console-toggle]")
  let current = Math.max(0, tabs.findIndex((tab) => tab.getAttribute("aria-selected") === "true"))
  let timer = null

  root.style.setProperty("--dwell", `${DWELL_MS}ms`)

  const autoplay = () => root.hasAttribute("data-autoplay")

  function startDwell() {
    const tab = tabs[current]
    tabs.forEach((t) => t.removeAttribute("data-run"))
    if (!autoplay()) return
    void tab.offsetWidth // restart the progress animation
    tab.setAttribute("data-run", "")
  }

  function typeOut(panel) {
    clearTimeout(timer)
    const q = panel.querySelector("[data-console-q]")
    const full = (q.dataset.full ??= q.textContent)
    panel.removeAttribute("data-playing")
    panel.setAttribute("data-armed", "")
    panel.setAttribute("data-typing", "")
    q.textContent = ""
    let n = 0
    const step = () => {
      q.textContent = full.slice(0, ++n)
      if (n < full.length) { timer = setTimeout(step, 18 + Math.random() * 22); return }
      timer = setTimeout(() => {
        panel.removeAttribute("data-armed")
        panel.setAttribute("data-playing", "")
        countUp(panel)
        startDwell()
        timer = setTimeout(() => panel.removeAttribute("data-typing"), 1600)
      }, 200)
    }
    timer = setTimeout(step, 200)
  }

  function select(i, { focus = false } = {}) {
    if (i === current) return
    tabs.forEach((tab, j) => {
      tab.setAttribute("aria-selected", String(j === i))
      tab.tabIndex = j === i ? 0 : -1
      tab.removeAttribute("data-run")
    })
    panels.forEach((panel, j) => {
      panel.removeAttribute("data-intro")
      if (j === i) panel.removeAttribute("data-off")
      else panel.setAttribute("data-off", "")
    })
    current = i
    if (focus) tabs[i].focus()
    revealTab(list, tabs[i])
    if (motion) typeOut(panels[i])
  }

  function stopAutoplay() {
    root.removeAttribute("data-autoplay")
    root.setAttribute("data-paused", "")
    tabs.forEach((t) => t.removeAttribute("data-run"))
    toggle.setAttribute("aria-label", "Play the examples")
  }

  tabs.forEach((tab, i) => tab.addEventListener("click", () => {
    if (motion) stopAutoplay()
    select(i)
  }))
  list.addEventListener("keydown", (event) => {
    const i = keyTarget(event.key, tabs.length, current, false)
    if (i === null) return
    event.preventDefault()
    if (motion) stopAutoplay()
    select(i, { focus: true })
  })

  if (!motion) return

  root.addEventListener("animationend", (event) => {
    if (event.animationName !== "console-progress" || !autoplay()) return
    select((current + 1) % tabs.length)
  })

  toggle.classList.replace("hidden", "flex")
  toggle.addEventListener("click", () => {
    if (!root.hasAttribute("data-paused")) {
      root.setAttribute("data-paused", "")
      toggle.setAttribute("aria-label", "Play the examples")
      return
    }
    root.removeAttribute("data-paused")
    toggle.setAttribute("aria-label", "Pause the examples")
    if (!autoplay()) {
      root.setAttribute("data-autoplay", "")
      startDwell()
    }
  })

  if ("IntersectionObserver" in window) {
    new IntersectionObserver(([entry]) => {
      root.toggleAttribute("data-offscreen", !entry.isIntersecting)
    }, { threshold: 0.4 }).observe(root)
  }

  root.setAttribute("data-autoplay", "")
  startDwell()
}

// The feature carousels (pages/carousel). The track is a native scroll-snap
// strip, so swipes, trackpads and the keyboard move it without help; this
// adds the arrows, turns the pills from page anchors into in-place scrolls,
// and follows the scroll position to keep pills and arrows in step.
function initCarousel(root, motion) {
  const track = root.querySelector("[data-carousel-track]")
  const slides = [...track.children]
  const pills = [...root.querySelectorAll("[data-carousel-to]")]
  const prev = root.querySelector("[data-carousel-prev]")
  const next = root.querySelector("[data-carousel-next]")
  let current = 0
  let queued = false

  const step = () => (slides[1] ? slides[1].offsetLeft - slides[0].offsetLeft : track.clientWidth)

  function go(i, instant = false) {
    const target = Math.max(0, Math.min(slides.length - 1, i))
    track.scrollTo({ left: target * step(), behavior: motion && !instant ? "smooth" : "auto" })
  }

  function sync() {
    queued = false
    current = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / step())))
    pills.forEach((pill, j) => pill.setAttribute("aria-current", String(j === current)))
    prev.disabled = current === 0
    next.disabled = current === slides.length - 1
    revealTab(pills[0].parentElement, pills[current])
  }

  track.addEventListener("scroll", () => {
    if (!queued) { queued = true; requestAnimationFrame(sync) }
  }, { passive: true })

  // Programmatic scrolls also fire "scroll", so the hand-over listens for
  // the gestures themselves: clicks, keys, a touch or press on the track, or
  // a sideways wheel (a vertical wheel over the track is page scrolling).
  // The scroll drive measures the slide card alone, not the heading above
  // it: a shorter element leaves more scroll per slide.
  const release = driveByScroll(track, slides.length, go)
  root.addEventListener("click", release)
  root.addEventListener("keydown", release)
  track.addEventListener("pointerdown", release)
  track.addEventListener("wheel", (event) => {
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) release()
  }, { passive: true })

  pills.forEach((pill, i) => pill.addEventListener("click", (event) => { event.preventDefault(); go(i) }))
  prev.addEventListener("click", () => go(current - 1))
  next.addEventListener("click", () => go(current + 1))
  root.addEventListener("keydown", (event) => {
    if (!event.target.closest("[data-carousel-to]")) return
    const i = keyTarget(event.key, slides.length, current, false)
    if (i === null) return
    event.preventDefault()
    go(i)
    pills[i].focus()
  })

  root.querySelector(".carousel-arrows").classList.remove("invisible")
  sync()
}

// "Every team's question": without JS every team is listed; here the list
// becomes tabs, with a bar on the left that slides to the chosen team.
function initTabs(root, motion) {
  const list = root.querySelector('[role="tablist"]')
  const tabs = [...list.querySelectorAll('[role="tab"]')]
  const panels = tabs.map((tab) => document.getElementById(tab.getAttribute("aria-controls")))
  const indicator = list.querySelector(".teams-indicator")
  let current = 0

  function place() {
    if (!indicator) return
    const tab = tabs[current]
    indicator.style.transform = `translateY(${tab.offsetTop}px)`
    indicator.style.height = `${tab.offsetHeight}px`
  }

  function select(i, { focus = false, animate = motion } = {}) {
    tabs.forEach((tab, j) => {
      tab.setAttribute("aria-selected", String(j === i))
      tab.tabIndex = j === i ? 0 : -1
    })
    panels.forEach((panel, j) => { panel.hidden = j !== i })
    current = i
    place()
    if (focus) tabs[i].focus()
    revealTab(list, tabs[i])
    if (animate) {
      const panel = panels[i]
      panel.removeAttribute("data-entering")
      void panel.offsetWidth
      panel.setAttribute("data-entering", "")
    }
  }

  root.setAttribute("data-tabs-ready", "")
  select(0, { animate: false })

  const release = driveByScroll(root, tabs.length, (i, instant) => select(i, { animate: motion && !instant }))
  list.addEventListener("click", release)
  list.addEventListener("keydown", release)

  tabs.forEach((tab, i) => tab.addEventListener("click", () => select(i)))
  list.addEventListener("keydown", (event) => {
    const i = keyTarget(event.key, tabs.length, current, true)
    if (i === null) return
    event.preventDefault()
    select(i, { focus: true })
  })
  window.addEventListener("resize", place)
}
